import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Search as SearchIcon,
    KeyboardArrowDown as ExpandIcon,
    KeyboardArrowUp as CollapseIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import shopOrderService from '../../../services/shopOrderService';
import { useCurrency } from '../../../hooks/useCurrency';
import DeliveryChip, { deliverySummary } from '../../common/DeliveryChip';
import { useDeliveryCompanies } from '../../../hooks/useDeliveryCompanies';

// Same palette convention as the website's tracking page's sub-order
// badges — kept visually consistent across admin and storefront.
const STATUS_COLORS = {
    PENDING: 'default',
    CONFIRMED: 'info',
    PROCESSING: 'info',
    PARTIALLY_SHIPPED: 'primary',
    SHIPPED: 'primary',
    DELIVERED: 'success',
    CANCELLED: 'error',
    RETURN_REQUESTED: 'warning',
    EXCHANGE_REQUESTED: 'warning',
    RETURNED: 'secondary',
    EXCHANGED: 'secondary',
};

// Item-level promotion breakdown — SubOrderDTO#items already carries
// promotionLabel/savedAmount per item (see ShopOrderService#createOrder,
// which persists it on StoreSubOrderItem regardless of promotion type,
// BUY_X_GET_Y included), but nothing rendered it here before: this screen
// only ever showed the sub-order's total amount, never why it was
// discounted. The real store Order (reachable via the "view store order"
// link) already shows this per-item breakdown too — this is the same data,
// shown here so an admin doesn't have to click through just to see it.
const SubOrderItemsList = ({ items, t }) => {
  const { format: formatMoney } = useCurrency();
  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
        {items.map((item, idx) => (
            <Box
                key={idx}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    py: 0.5,
                }}
            >
                <Box>
                    <Typography variant="body2">
                        {item.productName}
                        {item.variantAttributes ? ` — ${item.variantAttributes}` : ''}
                        {' '}× {item.quantity}
                    </Typography>
                    {item.promotionLabel && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                            <Chip label={item.promotionLabel} size="small" color="secondary" />
                            {item.savedAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                    {t('shopOrders.savings')}: -{formatMoney(item.savedAmount)}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    {item.savedAmount > 0 ? (
                        <>
                            <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {formatMoney(item.originalUnitPrice)}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" color="error.main">
                                {formatMoney(item.discountedUnitPrice)}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" fontWeight="medium">{formatMoney(item.unitPrice)}</Typography>
                    )}
                </Box>
            </Box>
        ))}
    </Box>
  );
};

const SubOrderRow = ({ subOrder, t, navigate }) => {
    const { format: formatMoney } = useCurrency();
    const [itemsOpen, setItemsOpen] = useState(false);
    const hasItems = (subOrder.items || []).length > 0;

    return (
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    py: 1,
                    px: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {hasItems && (
                        <IconButton size="small" onClick={() => setItemsOpen(o => !o)}>
                            {itemsOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </IconButton>
                    )}
                    <Box>
                        <Typography variant="body2" fontWeight="medium">{subOrder.storeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{subOrder.subOrderNumber}</Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={subOrder.statusLabel} color={STATUS_COLORS[subOrder.status] || 'default'} size="small" />
                    <Box sx={{ minWidth: 90, textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight="medium">{formatMoney(subOrder.amount)}</Typography>
                        {subOrder.totalSavedAmount > 0 && (
                            <Typography variant="caption" color="success.main" display="block">
                                -{formatMoney(subOrder.totalSavedAmount)}
                            </Typography>
                        )}
                    </Box>
                    {subOrder.storeOrderId && (
                        <IconButton
                            size="small"
                            title={t('shopOrders.viewStoreOrder')}
                            onClick={() => navigate(`/orders/${subOrder.storeOrderId}`)}
                        >
                            <OpenInNewIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </Box>
            {hasItems && (
                <Collapse in={itemsOpen} timeout="auto" unmountOnExit>
                    <SubOrderItemsList items={subOrder.items} t={t} />
                </Collapse>
            )}
        </Box>
    );
};

const ShopOrderRow = ({ order, t, navigate }) => {
    const { format: formatMoney } = useCurrency();
    const [open, setOpen] = useState(false);
    const storeNames = (order.subOrders || []).map(s => s.storeName);
    const uniqueStoreNames = [...new Set(storeNames)];

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(o => !o)}>
                        {open ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="medium">{order.orderNumber}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{order.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.customerEmail}</Typography>
                </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {uniqueStoreNames.map(name => (
                            <Chip key={name} label={name} size="small" variant="outlined" />
                        ))}
                    </Stack>
                </TableCell>
                <TableCell>
                    <Chip label={order.statusLabel} color={STATUS_COLORS[order.status] || 'default'} size="small" />
                </TableCell>
                <TableCell>
                    {order.deliveryMethod ? (
                        <Stack spacing={0.5} alignItems="flex-start">
                            <DeliveryChip order={order} />
                            <Typography variant="caption" color="text.secondary">{order.deliveryWilayaName}</Typography>
                        </Stack>
                    ) : '—'}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                        {formatMoney(order.totalAmount || 0)}
                    </Typography>
                </TableCell>
                <TableCell>
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={8} sx={{ py: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {order.deliveryMethod && (
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                                {deliverySummary(t, order)}
                            </Typography>
                        )}
                        <Box sx={{ my: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            {(order.subOrders || []).map(subOrder => (
                                <SubOrderRow key={subOrder.id} subOrder={subOrder} t={t} navigate={navigate} />
                            ))}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const ShopOrdersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { companies } = useDeliveryCompanies();
    const [orders, setOrders] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    // '' = all | HOME | a delivery company code - the list is fully loaded, so this filters client-side.
    const [deliveryFilter, setDeliveryFilter] = useState('');

    useEffect(() => { fetchOrders(); }, []);

    useEffect(() => {
        const term = searchTerm.trim().toLowerCase();
        setFiltered(orders.filter(o => {
            if (deliveryFilter === 'HOME' && o.deliveryMethod !== 'HOME') return false;
            if (deliveryFilter && deliveryFilter !== 'HOME' && o.deliveryCompany !== deliveryFilter) return false;
            if (!term) return true;
            return o.orderNumber?.toLowerCase().includes(term) ||
                o.customerName?.toLowerCase().includes(term) ||
                o.customerEmail?.toLowerCase().includes(term) ||
                o.deliveryWilayaName?.toLowerCase().includes(term);
        }));
    }, [searchTerm, deliveryFilter, orders]);

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const data = await shopOrderService.getShopOrders();
            setOrders(data || []);
        } catch (error) {
            toast.error(t('shopOrders.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4">{t('shopOrders.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('shopOrders.subtitle')}</Typography>
            </Box>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <TextField
                        fullWidth
                        placeholder={t('shopOrders.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                    />
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={deliveryFilter}
                        onChange={(_, next) => next !== null && setDeliveryFilter(next)}
                        sx={{ mt: 2 }}
                        aria-label={t('delivery.filterLabel')}
                    >
                        <ToggleButton value="">{t('delivery.filterAll')}</ToggleButton>
                        {companies.map((c) => (
                            <ToggleButton key={c.code} value={c.code}>{c.name}</ToggleButton>
                        ))}
                        <ToggleButton value="HOME">{t('delivery.home')}</ToggleButton>
                    </ToggleButtonGroup>
                </CardContent>
            </Card>

            <Card>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>{t('shopOrders.orderNumber')}</TableCell>
                                <TableCell>{t('shopOrders.customer')}</TableCell>
                                <TableCell>{t('shopOrders.stores')}</TableCell>
                                <TableCell>{t('common.status')}</TableCell>
                                <TableCell>{t('delivery.deliveryColumn')}</TableCell>
                                <TableCell>{t('shopOrders.totalAmount')}</TableCell>
                                <TableCell>{t('shopOrders.date')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center">{t('common.loading')}</TableCell></TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center">{t('common.noData')}</TableCell></TableRow>
                            ) : filtered.map((order) => (
                                <ShopOrderRow key={order.id} order={order} t={t} navigate={navigate} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
};

export default ShopOrdersList;
